import styled from "styled-components";
import Button from "./Button";
import { useCallback, useId, useRef, useState } from "react";
import Menu, {
  CALENDAR_CONTENT_OPTIONS,
  CALENDAR_SLIP_CONTENT_OPTIONS,
  TCalendarMenuOptions,
} from "./Menu";
import {
  handleParseQuerystrings,
  handlePopulateQuerystring,
  handleRevertTranslateCalendarOptions,
} from "../../../utils/miscellaneous";

const Container = styled.div`
  position: relative;
`;

export type TCommonProps = {
  content: string[];
  placeholder: string;
  search:
    | boolean
    | {
        placeholder: string;
        onChange?: ((searchPhrase: string) => void) | undefined;
        onClear?: (() => void) | undefined;
        onSearch?: ((searchPhrase: string) => void) | undefined;
      };
  optGroup?: "Últimos acessados";
  onSelect?: (select: string, allSelect: string[]) => void;
  buttonVariant: TIconVariants;
  customFilter?: (
    index: string,
    selected: string[],
    setSelected: React.Dispatch<React.SetStateAction<string[]>>,
    selectDataRef: React.MutableRefObject<string[]>,
    prevSelectDataRef: React.MutableRefObject<string[]>,
    setShouldUpdate: React.Dispatch<React.SetStateAction<boolean>>
  ) => void;
  onAction?: (selectDataRef: React.MutableRefObject<string[]>) => void;
  onClear?: () => void;
  onCancel?: (selectDataRef: React.MutableRefObject<string[]>) => void;
  isSingleSelect?: boolean;
  hasBottomBar?: boolean;
  forceSelectOptionsByIndex?: number[];
  filled?: string[];
  prevSelectData?: string[];
};

export type TCalendarProps = {
  content?: never;
  placeholder?: never;
  search?: never;
  optGroup?: never;
  onSelect?: (select: string[]) => void;
  buttonVariant: TIconVariants;
  customFilter?: never;
  onAction?: never;
  onCancel?: never;
  onClear?: () => void;
  isSingleSelect?: never;
  hasBottomBar?: never;
  forceSelectOptionsByIndex?: number[];
  filled?: string[];
  prevSelectData?: string[];
};

export type TVariants = "labeled" | "unlabeled" | "calendar" | "calendar-slip";

export type TIconVariants = "calendar" | "infoCircle" | "checkCircle" | "eye";

export type TLabeled = {
  menuVariant: "labeled";
  label: string[];
};

export type TUnlabeled = {
  menuVariant: "unlabeled";
  label?: never;
};

type TCalendar = {
  menuVariant: "calendar" | "calendar-slip";
  label?: never;
};

export type TDropdownProps<Variant extends TVariants> =
  Variant extends "labeled"
    ? TLabeled & TCommonProps
    : Variant extends "calendar"
    ? TCalendar & TCalendarProps
    : TUnlabeled & TCommonProps;

const DropdownFilter: React.FC<TDropdownProps<TVariants>> = ({
  menuVariant,
  content,
  placeholder,
  label,
  search,
  optGroup,
  onSelect,
  onAction,
  onCancel,
  onClear,
  buttonVariant,
  customFilter,
  isSingleSelect,
  hasBottomBar,
  forceSelectOptionsByIndex,
  filled,
}) => {
  const [selectedDataLength, setSelectedDataLength] = useState(
    `${filled?.length ?? ""}`
  );
  const [selectedCalendarFilter, setSelectedCalendarFilter] = useState(
    filled
      ? filled.join(" - ")
      : menuVariant.includes("slip")
      ? CALENDAR_SLIP_CONTENT_OPTIONS[0]
      : CALENDAR_CONTENT_OPTIONS[0]
  );

  const [shouldShow, setShouldShow] = useState<"show" | "hide">("hide");
  const [isFiltered, setIsFiltered] = useState(
    filled !== undefined && filled.length > 0 ? true : false
  );
  const [shouldUpdate, setShouldUpdate] = useState(false);

  const selectDataRef = useRef([""]);
  const prevSelectDataRef = useRef([""]);

  const setSelectDataRef = useRef<
    React.Dispatch<React.SetStateAction<string[]>> | undefined
  >();

  const setShouldClearCalendarRef = useRef<
    React.Dispatch<React.SetStateAction<boolean>> | undefined
  >();

  const setCalendarSelectedDatesRef = useRef<
    | React.Dispatch<
        React.SetStateAction<{
          startDate: string;
          endDate: string;
        }>
      >
    | undefined
  >();

  const actionButtonIdRef = useRef("");

  const componentRef = useRef<HTMLDivElement | null>(null);
  const buttonSetClickStateRef = useRef<React.Dispatch<
    React.SetStateAction<boolean>
  > | null>(null);

  const searchId = search ? useId() : "";

  const menuId = useId();

  const handleRenderDropdown = useCallback(() => {
    switch (menuVariant) {
      case "labeled":
        const labeledButtonRef = useRef<HTMLButtonElement>(null);

        const handleLabeledOnClickButton = () => {
          if (setSelectDataRef.current) setSelectDataRef.current([]);
          setShouldShow((prev) => (prev === "hide" ? "show" : "hide"));
        };

        const handleLabeledOnClickMenu = (
          index: string,
          selected: string[],
          setSelected: React.Dispatch<React.SetStateAction<string[]>>
        ) => {
          if (selected.includes(index)) {
            setSelected((prev) => {
              const value = prev.splice(prev.indexOf(index), 1);

              return value;
            });
          } else {
            setSelected((prev) => {
              const value = [...prev, index];
              return value;
            });
          }
        };
        return (
          <Container ref={componentRef}>
            <Button
              componentRef={componentRef}
              $ref={labeledButtonRef}
              onClick={handleLabeledOnClickButton}
              placeholder={placeholder}
              variant={buttonVariant}
              content={selectedDataLength.replace(/.*?%(.*)/, "$1")}
              isFiltered={isFiltered}
            />
            <Menu
              searchId={searchId}
              shouldShow={shouldShow}
              variant="labeled"
              search={search}
              label={label}
              content={content}
              list="filter"
              onClick={handleLabeledOnClickMenu}
              onClear={onClear}
              optGroup={optGroup}
              hasBottomBar={hasBottomBar}
              forceSelectOptionsByIndex={forceSelectOptionsByIndex}
            />
          </Container>
        );
      case "unlabeled":
        const unlabeledButtonRef = useRef<HTMLButtonElement | null>(null);

        const handleUnlabeledOnClickButton = (
          setClickState: React.Dispatch<React.SetStateAction<boolean>>
        ) => {
          buttonSetClickStateRef.current = setClickState;

          setShouldShow((prev) => {
            if (prev === "hide") {
              setTimeout(() => {
                unlabeledButtonRef.current?.classList.add("active");
              }, 30);
              return "show";
            } else {
              handleUnlabeledOnBlur();
              setTimeout(() => {
                unlabeledButtonRef.current?.classList.remove("active");
              }, 30);
              return "hide";
            }
          });
        };

        const handleUnlabeledOnActionMenu = (
          _select: string[],
          actionButtonId: string
        ) => {
          setTimeout(() => {
            unlabeledButtonRef.current?.classList.remove("active");
          }, 30);

          if (setSelectDataRef.current)
            setSelectDataRef.current(selectDataRef.current);
          if (actionButtonId) actionButtonIdRef.current = actionButtonId;

          if (`${selectDataRef.current.length}` !== selectedDataLength)
            setSelectedDataLength(`${selectDataRef.current.length}`);
          if (onAction) onAction(selectDataRef);
          setIsFiltered(true);
          handleUnlabeledOnBlur();
        };

        const handleUnlabeledOnCancelMenu = () => {
          if (onCancel) onCancel(selectDataRef);
          handleUnlabeledOnBlur();
        };

        const handleUnlabeledOnClearMenu = () => {
          if (onClear) onClear();
          handleUnlabeledOnBlur();
        };

        const handleUnlabeledQueryParams = () => {
          const searchStrings = handlePopulateQuerystring();

          const roleArray = Array.from(searchStrings.entries())
            .filter((value) => value[0] === "role")
            .map((value) => value[1]);

          const statusArray = Array.from(searchStrings.entries())
            .filter((value) => value[0] === "status")
            .map((value) => value[1]);

          let qs = [];

          if (
            (searchStrings.has("has_accounts_payable") &&
              searchStrings.get("has_accounts_payable") === "true") ||
            (placeholder.toLowerCase() === "status" &&
              searchStrings.has("status") &&
              statusArray.includes("1")) ||
            (placeholder.toLowerCase() === "perfil de acesso" &&
              searchStrings.has("role") &&
              roleArray.includes("30"))
          ) {
            qs.push("1");
          }

          if (
            (searchStrings.has("has_goods_receipt") &&
              searchStrings.get("has_goods_receipt") === "true") ||
            (placeholder.toLowerCase() === "status" &&
              searchStrings.has("status") &&
              statusArray.includes("2")) ||
            (placeholder.toLowerCase() === "perfil de acesso" &&
              searchStrings.has("role") &&
              roleArray.includes("20"))
          ) {
            qs.push("2");
          }

          if (
            (placeholder.toLowerCase() === "status" &&
              searchStrings.has("status") &&
              statusArray.includes("0")) ||
            (placeholder.toLowerCase() === "perfil de acesso" &&
              searchStrings.has("role") &&
              roleArray.includes("10"))
          ) {
            qs.push("3");
          }

          if (
            (searchStrings.has("has_accounts_payable") &&
              searchStrings.has("has_goods_receipt") &&
              searchStrings.get("has_accounts_payable") === "false" &&
              searchStrings.get("has_goods_receipt") === "false") ||
            (placeholder.toLowerCase() === "status" &&
              searchStrings.has("status") &&
              statusArray.includes("3")) ||
            (placeholder.toLowerCase() === "perfil de acesso" &&
              searchStrings.has("role") &&
              roleArray.includes("40"))
          ) {
            qs.push("0");
          }

          // Notas emitidas: filtros "Natureza" (nature=1..15) e "Status"
          // (status=10..70, emission_status). A heurística legada acima só
          // cobre recebidas (status 0..3). Aqui mapeamos os valores das
          // emitidas para o índice da opção (sem quebrar o legado).
          const natureArray = Array.from(searchStrings.entries())
            .filter((v) => v[0] === "nature")
            .map((v) => v[1]);
          if (placeholder.toLowerCase() === "natureza" && natureArray.length) {
            qs = natureArray
              .map((n) => parseInt(n))
              .filter((n) => !isNaN(n) && n >= 1 && n <= 15)
              .map((n) => `${n - 1}`); // nature N -> índice N-1
          }
          // Status das emitidas: valores múltiplos de 10 (10..70).
          const emissionStatus = statusArray
            .map((s) => parseInt(s))
            .filter((s) => !isNaN(s) && s >= 10 && s % 10 === 0);
          if (placeholder.toLowerCase() === "status" && emissionStatus.length) {
            qs = emissionStatus.map((s) => `${s / 10 - 1}`); // 10->0,20->1,...
          }

          prevSelectDataRef.current = qs;
        };

        const handleUnlabeledOnBlur = (): "show" | "hide" => {
          handleUnlabeledQueryParams();
          setTimeout(() => {
            if (buttonSetClickStateRef.current)
              buttonSetClickStateRef.current(false);
            if (setSelectDataRef.current) {
              setSelectDataRef.current(prevSelectDataRef.current);
            }
          }, 20);

          setShouldShow("hide");
          return "hide";
        };

        const handleUnlabeledOnClickMenu = (
          index: string,
          selected: string[],
          setSelected: React.Dispatch<React.SetStateAction<string[]>>
        ) => {
          if (customFilter) {
            customFilter(
              index,
              selected,
              setSelected,
              selectDataRef,
              prevSelectDataRef,
              setShouldUpdate
            );
            setSelectDataRef.current = setSelected;
          } else {
            if (index === `0`) {
              setSelected([""]);
              setSelectedDataLength("");
              if (onSelect) onSelect(content[parseInt(index)], [""]);
            } else if (selected.includes(index)) {
              setSelected((prev) => {
                const deleteItemIndex = prev.indexOf(index);
                prev.splice(deleteItemIndex, deleteItemIndex > -1 ? 1 : 0);

                const value = prev;
                if (value[0] === "") value.splice(0, 1);

                setSelectedDataLength(`${value.length}`);
                if (onSelect) onSelect(content[parseInt(index)], value);
                return value;
              });
            } else {
              setSelected((prev) => {
                let value;
                if (isSingleSelect) {
                  value = [index];
                  setSelectedDataLength(`${value.length}`);
                  if (onSelect) onSelect(content[parseInt(index)], value);
                } else {
                  value = [...prev, index];

                  if (value[0] === "") value.splice(0, 1);

                  if (value.length === content.length - 1) {
                    value = [""];
                    setSelectedDataLength("");
                    if (onSelect) onSelect(content[0], value);
                  } else {
                    setSelectedDataLength(`${value.length}`);
                    if (onSelect) onSelect(content[parseInt(index)], value);
                  }
                }
                return value;
              });
            }
          }
        };

        return (
          <Container ref={componentRef}>
            <Button
              componentRef={componentRef}
              $ref={unlabeledButtonRef}
              onBlur={handleUnlabeledOnBlur}
              onClick={handleUnlabeledOnClickButton}
              placeholder={placeholder}
              variant={buttonVariant}
              content={selectedDataLength}
              isFiltered={isFiltered}
            />
            <Menu
              menuId={menuId}
              searchId={searchId}
              search={search}
              shouldShow={shouldShow}
              variant="unlabeled"
              content={content}
              list="filter"
              onClick={handleUnlabeledOnClickMenu}
              optGroup={optGroup}
              onCancel={handleUnlabeledOnCancelMenu}
              onAction={handleUnlabeledOnActionMenu}
              onClear={handleUnlabeledOnClearMenu}
              hasBottomBar={hasBottomBar}
              forceSelectOptionsByIndex={forceSelectOptionsByIndex}
            />
          </Container>
        );
      case "calendar":
      case "calendar-slip":
        const calendarButtonRef = useRef<HTMLButtonElement>(null);

        const handleCalendarOnClickButton = (
          setClickState: React.Dispatch<React.SetStateAction<boolean>>
        ) => {
          setTimeout(() => {
            buttonSetClickStateRef.current = setClickState;
            calendarButtonRef.current?.classList.add("active");

            setShouldShow((prev) => {
              if (prev === "show") {
                handleCalendarOnBlur();
              }
              return prev === "hide" ? "show" : "hide";
            });
          }, 30);
        };

        const handleCalendarOnActionMenu = (selected: string[]) => {
          setTimeout(() => {
            calendarButtonRef.current?.classList.remove("active");
          }, 50);

          if (onSelect) onSelect(selected);
          setIsFiltered(true);
          const value =
            selected[0].length > 0
              ? selected[0]
              : menuVariant.includes("slip")
              ? CALENDAR_SLIP_CONTENT_OPTIONS[0]
              : CALENDAR_CONTENT_OPTIONS[0];

          setSelectedCalendarFilter(value);
          setShouldShow("hide");
        };

        const handleCalendarOnClearMenu = () => {
          setShouldShow("hide");

          setTimeout(() => {
            calendarButtonRef.current?.classList.remove("active");
            if (onClear) {
              onClear();
            }
          }, 20);
        };

        const handleCalendarOnCancelMenu = () => {
          setShouldShow("hide");

          const queryStrings = handleCalendarQueryParams();

          setTimeout(() => {
            calendarButtonRef.current?.classList.remove("active");

            if (queryStrings.end_date && queryStrings.start_date) {
              prevSelectDataRef.current = [
                queryStrings.end_date[0].replace(
                  /(\d{4})-(\d{2})-(\d{2})/,
                  "$3/$2/$1"
                ),
                queryStrings.start_date[0].replace(
                  /(\d{4})-(\d{2})-(\d{2})/,
                  "$3/$2/$1"
                ),
              ];
            }

            if (setSelectDataRef.current)
              setSelectDataRef.current(prevSelectDataRef.current);

            if (queryStrings.period) {
              if (setCalendarSelectedDatesRef.current) {
                setCalendarSelectedDatesRef.current({
                  endDate: "",
                  startDate: "",
                });
              }
            }

            if (!queryStrings.period && !queryStrings.end_date) {
              if (setCalendarSelectedDatesRef.current) {
                setCalendarSelectedDatesRef.current({
                  endDate: "",
                  startDate: "",
                });
              }
            }
          }, 20);
        };

        const handleCalendarQueryParams = () => {
          const queryStrings = handleParseQuerystrings(window.location.search);

          const calendarOptions = window.location.pathname.includes(
            "payment-slip"
          )
            ? [
                ...CALENDAR_SLIP_CONTENT_OPTIONS.map((option) =>
                  option.toLowerCase()
                ),
              ]
            : [
                ...CALENDAR_CONTENT_OPTIONS.map((option) =>
                  option.toLowerCase()
                ),
              ];

          if (queryStrings.period) {
            const qs = handleRevertTranslateCalendarOptions(
              queryStrings.period[0] as TCalendarMenuOptions
            );
            if (window.location.pathname.includes("payment-slip")) {
              prevSelectDataRef.current = [
                CALENDAR_SLIP_CONTENT_OPTIONS[
                  calendarOptions.indexOf(qs as string)
                ],
              ];
            } else {
              prevSelectDataRef.current = [
                CALENDAR_CONTENT_OPTIONS[calendarOptions.indexOf(qs as string)],
              ];
            }
          }

          if (queryStrings.end_date && queryStrings.start_date) {
            prevSelectDataRef.current = [
              queryStrings.end_date[0],
              queryStrings.start_date[0],
            ];
          }

          if (!queryStrings.period && !queryStrings.end_date) {
            prevSelectDataRef.current = [CALENDAR_CONTENT_OPTIONS[0]];
          }

          return queryStrings;
        };

        const handleCalendarOnBlur = (): "hide" | "show" => {
          const queryStrings = handleCalendarQueryParams();

          setShouldShow("hide");

          setTimeout(() => {
            if (/* !isOnActionRef.current && */ setSelectDataRef.current) {
              if (queryStrings.end_date && queryStrings.start_date) {
                prevSelectDataRef.current = [
                  queryStrings.end_date[0].replace(
                    /(\d{4})-(\d{2})-(\d{2})/,
                    "$3/$2/$1"
                  ),
                  queryStrings.start_date[0].replace(
                    /(\d{4})-(\d{2})-(\d{2})/,
                    "$3/$2/$1"
                  ),
                ];
              } else if (queryStrings.period) {
                let calendarOptions;
                if (menuVariant === "calendar") {
                  calendarOptions = [
                    ...CALENDAR_CONTENT_OPTIONS.map((option) =>
                      option.toLowerCase()
                    ),
                  ];
                } else {
                  calendarOptions = [
                    ...CALENDAR_SLIP_CONTENT_OPTIONS.map((option) =>
                      option.toLowerCase()
                    ),
                  ];
                }

                const period = handleRevertTranslateCalendarOptions(
                  queryStrings.period[0] as TCalendarMenuOptions
                );
                if (period)
                  prevSelectDataRef.current =
                    menuVariant === "calendar"
                      ? [
                          CALENDAR_CONTENT_OPTIONS[
                            calendarOptions.indexOf(period)
                          ],
                        ]
                      : [
                          CALENDAR_SLIP_CONTENT_OPTIONS[
                            calendarOptions.indexOf(period)
                          ],
                        ];
              }

              setSelectDataRef.current(prevSelectDataRef.current);

              if (queryStrings.period) {
                if (setCalendarSelectedDatesRef.current) {
                  setCalendarSelectedDatesRef.current({
                    endDate: "",
                    startDate: "",
                  });
                }
              }

              if (!queryStrings.period && !queryStrings.end_date) {
                if (setCalendarSelectedDatesRef.current) {
                  setCalendarSelectedDatesRef.current({
                    endDate: "",
                    startDate: "",
                  });
                }
              }

              if (buttonSetClickStateRef.current)
                buttonSetClickStateRef.current(false);
            }
          }, 20);
          return "hide";
        };

        const handleCalendarOnClickMenu = (
          _index: string,
          selected: string[],
          setSelected: React.Dispatch<React.SetStateAction<string[]>>,
          setShouldClearCalendar: React.Dispatch<React.SetStateAction<boolean>>
        ) => {
          setSelected(selected);

          if (setCalendarSelectedDatesRef.current)
            setCalendarSelectedDatesRef.current((before) => {
              if (
                prevSelectDataRef.current[0] === "" &&
                filled?.length === 2 &&
                selected.length !== 2
              ) {
                return { endDate: "", startDate: "" };
              }

              return before;
            });

          setSelectDataRef.current = setSelected;
          setShouldClearCalendarRef.current = setShouldClearCalendar;
        };

        return (
          <Container ref={componentRef}>
            <Button
              componentRef={componentRef}
              $ref={calendarButtonRef}
              onBlur={handleCalendarOnBlur}
              onClick={handleCalendarOnClickButton}
              placeholder={selectedCalendarFilter}
              variant={buttonVariant}
              content={selectedCalendarFilter}
              isFiltered={isFiltered}
            />
            <Menu
              shouldShow={shouldShow}
              variant={menuVariant}
              list="filter"
              onClick={handleCalendarOnClickMenu}
              optGroup={optGroup}
              onCancel={handleCalendarOnCancelMenu}
              onAction={handleCalendarOnActionMenu}
              onClear={handleCalendarOnClearMenu}
              forceSelectOptionsByIndex={forceSelectOptionsByIndex}
              filled={
                setShouldClearCalendarRef.current
                  ? undefined
                  : prevSelectDataRef.current.length === 2
                  ? prevSelectDataRef.current
                  : filled
              }
              setSelectedDatesRef={setCalendarSelectedDatesRef}
            />
          </Container>
        );
    }
  }, [
    shouldShow,
    menuVariant,
    selectedDataLength,
    selectedCalendarFilter,
    shouldUpdate,
    content,
  ]);

  return handleRenderDropdown();
};

export default DropdownFilter;
